import type { PrimeVueLocaleOptions } from 'primevue/config'

import type { Language } from '@/config/lang'

/**
 * Тексты самих компонентов PrimeVue: календарь, пустые списки, кнопки
 * подтверждения. Живут отдельно от словарей приложения — их формат задаёт
 * PrimeVue, а не мы, и подставляются они через `options.locale`.
 */
const ru: Partial<PrimeVueLocaleOptions> = {
  startsWith: 'Начинается с',
  contains: 'Содержит',
  emptySearchMessage: 'Ничего не найдено',
  emptySelectionMessage: 'Ничего не выбрано',
  emptyMessage: 'Список пуст',
  accept: 'Да',
  reject: 'Нет',
  clear: 'Очистить',
  apply: 'Применить',
  choose: 'Выбрать',
  upload: 'Загрузить',
  cancel: 'Отмена',
  completed: 'Завершено',
  pending: 'В процессе',
  dayNames: ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'],
  dayNamesShort: ['Вск', 'Пнд', 'Втр', 'Срд', 'Чтв', 'Птн', 'Сбт'],
  dayNamesMin: ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
  monthNames: [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
  ],
  monthNamesShort: [
    'Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн',
    'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек',
  ],
  /* Подписи навигации календаря — их читает screen reader на стрелках и
     кнопках месяца с годом в шите «Дата рождения» */
  chooseYear: 'Выберите год',
  chooseMonth: 'Выберите месяц',
  chooseDate: 'Выберите дату',
  prevDecade: 'Предыдущее десятилетие',
  nextDecade: 'Следующее десятилетие',
  prevYear: 'Предыдущий год',
  nextYear: 'Следующий год',
  prevMonth: 'Предыдущий месяц',
  nextMonth: 'Следующий месяц',
  today: 'Сегодня',
  weekHeader: 'Нед',
  firstDayOfWeek: 1,
  dateFormat: 'dd.mm.yy',
}

const uz: Partial<PrimeVueLocaleOptions> = {
  startsWith: 'Boshlanadi',
  contains: 'Tarkibida',
  emptySearchMessage: 'Hech narsa topilmadi',
  emptySelectionMessage: 'Hech narsa tanlanmadi',
  emptyMessage: "Ro'yxat bo'sh",
  accept: 'Ha',
  reject: "Yo'q",
  clear: 'Tozalash',
  apply: "Qo'llash",
  choose: 'Tanlash',
  upload: 'Yuklash',
  cancel: 'Bekor qilish',
  completed: 'Yakunlandi',
  pending: 'Jarayonda',
  dayNames: ['Yakshanba', 'Dushanba', 'Seshanba', 'Chorshanba', 'Payshanba', 'Juma', 'Shanba'],
  dayNamesShort: ['Yak', 'Dush', 'Sesh', 'Chor', 'Pay', 'Jum', 'Shan'],
  dayNamesMin: ['Ya', 'Du', 'Se', 'Ch', 'Pa', 'Ju', 'Sh'],
  monthNames: [
    'Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'Iyun',
    'Iyul', 'Avgust', 'Sentabr', 'Oktabr', 'Noyabr', 'Dekabr',
  ],
  monthNamesShort: [
    'Yan', 'Fev', 'Mar', 'Apr', 'May', 'Iyn',
    'Iyl', 'Avg', 'Sen', 'Okt', 'Noy', 'Dek',
  ],
  chooseYear: 'Yilni tanlang',
  chooseMonth: 'Oyni tanlang',
  chooseDate: 'Sanani tanlang',
  prevDecade: "O'tgan o'n yillik",
  nextDecade: "Keyingi o'n yillik",
  prevYear: "O'tgan yil",
  nextYear: 'Keyingi yil',
  prevMonth: "O'tgan oy",
  nextMonth: 'Keyingi oy',
  today: 'Bugun',
  weekHeader: 'Hafta',
  firstDayOfWeek: 1,
  dateFormat: 'dd.mm.yy',
}

const en: Partial<PrimeVueLocaleOptions> = {
  startsWith: 'Starts with',
  contains: 'Contains',
  emptySearchMessage: 'No results found',
  emptySelectionMessage: 'No selection',
  emptyMessage: 'The list is empty',
  accept: 'Yes',
  reject: 'No',
  clear: 'Clear',
  apply: 'Apply',
  choose: 'Choose',
  upload: 'Upload',
  cancel: 'Cancel',
  completed: 'Completed',
  pending: 'Pending',
  dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
  monthNames: [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ],
  monthNamesShort: [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ],
  chooseYear: 'Choose year',
  chooseMonth: 'Choose month',
  chooseDate: 'Choose date',
  prevDecade: 'Previous decade',
  nextDecade: 'Next decade',
  prevYear: 'Previous year',
  nextYear: 'Next year',
  prevMonth: 'Previous month',
  nextMonth: 'Next month',
  today: 'Today',
  weekHeader: 'Wk',
  firstDayOfWeek: 1,
  dateFormat: 'dd.mm.yy',
}

export const PRIME_LOCALES: Record<Language, Partial<PrimeVueLocaleOptions>> = { ru, uz, en }
